import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { getFormattedAddress, getFormattedDateAndTime, AppointmentBookingHours } from '@modmd/data'
import { COMPANY_ROUTES, ROUTES } from 'client-portal/constants/routes'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { Box, Container } from 'components/Layout'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Text } from 'components/Typography'
import { DEVICE, pxToRem, COLOR } from 'theme'
import {
  RequestTestSession,
  RequestProps,
} from 'sharedComponents/RequestGroupTestSession/RequestTestSession'
import Button from 'components/Button'

interface RouterProps {
  companyId: string
  groupId: string
}

type Props = RouteComponentProps<RouterProps>

const RequestGroupTestSession: React.VFC<Props> = ({ history, match }) => {
  const { groupId, companyId } = match.params
  const [info, setInfo] = React.useState<
    { date?: string; Address?: string; sessionId: string } | undefined
  >(undefined)
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const handleGroupRequestSuccess = React.useCallback(
    ({ reqSessionFormValues, sessionId }: RequestProps) => {
      const { date, Address } = reqSessionFormValues
      const formattedAddress = getFormattedAddress(Address)
      const dateAndTime = date
        ? getFormattedDateAndTime(date, true)?.split(' ').join(' at ')
        : undefined
      setInfo({ date: dateAndTime, Address: formattedAddress, sessionId })
    },
    []
  )

  const handleRedirect = React.useCallback(() => {
    setInfo(undefined)
    if (info?.sessionId) {
      history.push(
        `/${ROUTES.COMPANY}/${companyId}/${COMPANY_ROUTES.TEST_SESSIONS}/${info.sessionId}`
      )
    }
  }, [companyId, history, info?.sessionId])

  return (
    <CompanyPageLayout>
      <Container>
        <Card>
          <Card.Title>Group Test Session Form</Card.Title>
          <Card.Content>
            <RequestTestSession
              companyId={companyId}
              onRequestSuccess={({ reqSessionFormValues, sessionId }) =>
                handleGroupRequestSuccess({ reqSessionFormValues, sessionId })
              }
              groupId={groupId}
              minHoursBeforeBooking={AppointmentBookingHours.COMPANY}
            />
          </Card.Content>
        </Card>
        <Dialog
          variant={isMobile ? 'fullscreen' : 'center'}
          maxWidth={pxToRem(400)}
          isOpen={!!info}
          onDismiss={handleRedirect}
        >
          <Box flexDirection="column" width="100%">
            <Card.Title onDismiss={handleRedirect}>Thank you</Card.Title>
            <Card.Content>
              <Text color={COLOR.brand}>
                You have requested a test session on {info?.date} at {info?.Address}. Our scheduling
                team will contact you to confirm the appointment or if we need to change any of the
                details.
              </Text>
            </Card.Content>
            <Card.Actions>
              <Button onClick={handleRedirect}>Confirm</Button>
            </Card.Actions>
          </Box>
        </Dialog>
      </Container>
    </CompanyPageLayout>
  )
}

export { RequestGroupTestSession }
